import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import isEqual from 'lodash/isEqual'

import { useSetCasinoFlashPrizeGames, useSetCasinoFlashPrizes } from '../../../store/casinoStore/casinoActions'
import { useCasinoStore, usePlayerStore } from '../../../store'
import { useConfigValueByCurrentDevice } from '../../../../hooks/useConfigValueByCurrentDevice'
import { config } from '../../../../components/Init/Init'

export const useUpdateFlashPrizes = firebaseCurrentUser => {
  const playerStore = usePlayerStore()
  const playerLoginUser = playerStore.use.loginUser()

  const casinoStore = useCasinoStore()
  const casinoFlashPrizes = casinoStore.use.casinoFlashPrizes()
  const casinoFlashPrizeGames = casinoStore.use.casinoFlashPrizeGames()

  const [flashPrizesIntervalListening, setFlashPrizesIntervalListening] = useState(false)
  const flashPrizesQueryInterval = useRef(null)
  const flashPrizesQueryTimer = useRef(null)
  const isFlashPrizesFirebaseListeningAvailable = useRef(false)
  const { setCasinoFlashPrizes } = useSetCasinoFlashPrizes()
  const { setCasinoFlashPrizeGames } = useSetCasinoFlashPrizeGames()

  const flashPrizesQueryIntervalMs = useConfigValueByCurrentDevice(config.flashPrizesQueryIntervalMs)
  const enableFlashPrizesFirebaseListening = useConfigValueByCurrentDevice(config.enableFlashPrizesFirebaseListening)
  const flashPrizesQueryDelayMs = useConfigValueByCurrentDevice(config.flashPrizesQueryDelayMs)

  useEffect(() => {
    isFlashPrizesFirebaseListeningAvailable.current =
      config.enableSweepstakes &&
      playerLoginUser &&
      playerLoginUser.userId &&
      firebaseCurrentUser &&
      window.rmgBase &&
      enableFlashPrizesFirebaseListening
  }, [enableFlashPrizesFirebaseListening, window.rmgBase, firebaseCurrentUser, playerLoginUser.userId])

  const updateFlashPrizes = useCallback(() => {
    if (window.rmgBase) {
      const db = window.rmgBase.database()

      db.ref(`flashPrizes`)
        .once('value')
        .then(snapshot => {
          const flashPrizes = snapshot.val()
          if (!isEqual(casinoFlashPrizes, flashPrizes)) {
            setCasinoFlashPrizes(flashPrizes)
          }

          const flashPrizeGames = {}

          flashPrizes &&
            flashPrizes.forEach(flashPrize => {
              const startDate = flashPrize.startDate
              const endDate = flashPrize.endDate
              const currencies = flashPrize.currencies ? flashPrize.currencies : []
              const nowMs = new Date().getTime()
              const endDateMS = new Date(endDate).getTime()

              flashPrize.campaignGames &&
                flashPrize.campaignGames.forEach(({ gameId, hasDiamondReward }) => {
                  flashPrizeGames[gameId] = {
                    gameId,
                    endDate,
                    startDate,
                    currencies,
                    hasDiamondReward,
                    isExpired: endDateMS < nowMs
                  }
                })
            })

          if (!isEqual(casinoFlashPrizeGames, flashPrizeGames)) {
            setCasinoFlashPrizeGames(flashPrizeGames)
          }
        })
        .catch(error => {
          console.log('Failed to fetch flash prizes data FB user:', firebaseCurrentUser)
          console.error('Failed to fetch flash prizes data:', error.message)
        })
    }
  }, [casinoFlashPrizeGames, casinoFlashPrizes])

  useEffect(() => {
    const initFlashPrizesQueryInterval = () => {
      if (
        isFlashPrizesFirebaseListeningAvailable.current &&
        flashPrizesIntervalListening &&
        !flashPrizesQueryInterval.current
      ) {
        flashPrizesQueryInterval.current = setInterval(updateFlashPrizes, flashPrizesQueryIntervalMs)
      }
    }

    if (flashPrizesQueryInterval.current) {
      clearInterval(flashPrizesQueryInterval.current)
      flashPrizesQueryInterval.current = null

      initFlashPrizesQueryInterval()
    } else {
      flashPrizesQueryTimer.current = setTimeout(() => {
        initFlashPrizesQueryInterval()
      }, flashPrizesQueryDelayMs || 0)
    }

    return () => {
      clearTimeout(flashPrizesQueryTimer.current)
      clearInterval(flashPrizesQueryInterval.current)
    }
  }, [updateFlashPrizes, flashPrizesQueryDelayMs, flashPrizesQueryIntervalMs, flashPrizesIntervalListening])

  const initFlashPrizesFirebaseListening = useCallback(() => {
    if (isFlashPrizesFirebaseListeningAvailable.current && !flashPrizesQueryInterval.current) {
      const timer = setTimeout(() => {
        updateFlashPrizes()
        setFlashPrizesIntervalListening(true)
        clearTimeout(timer)
      }, flashPrizesQueryDelayMs || 0)
    }
  }, [flashPrizesQueryDelayMs, updateFlashPrizes])

  return { initFlashPrizesFirebaseListening, flashPrizesQueryInterval }
}
